import { HotelQuote, PriceQuoteResponse } from "@/types/price-quote"

import { baseApi } from "./base-api"

export type { PriceQuoteResponse }

export interface GetPriceQuoteArgs {
  ref: string // e.g., "hq-4TBOV0mL" or "fq-abc12345"
  shareCode?: string
}

export const priceQuotesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPriceQuote: builder.query<PriceQuoteResponse, GetPriceQuoteArgs>({
      query: ({ ref, shareCode }) => ({
        url: `/api/price_quotes/${ref}`,
        params: shareCode ? { share_code: shareCode } : undefined,
      }),
      transformResponse: (response: { data: PriceQuoteResponse }) =>
        response.data,
      providesTags: (result, _error, { ref }) =>
        result ? [{ type: "PriceQuote", id: ref }] : [],
    }),

    // Separate endpoint for live-price-enriched fetch. This hits the external
    // provider and is slower than getPriceQuote, so it has its own cache entry.
    getPriceQuoteWithLivePrice: builder.query<
      PriceQuoteResponse,
      GetPriceQuoteArgs
    >({
      query: ({ ref, shareCode }) => ({
        url: `/api/price_quotes/${ref}`,
        params: {
          fetch_live_price: "true",
          ...(shareCode ? { share_code: shareCode } : {}),
        },
      }),
      transformResponse: (response: { data: PriceQuoteResponse }) =>
        response.data,
      providesTags: (result, _error, { ref }) =>
        result ? [{ type: "PriceQuote", id: `${ref}-live` }] : [],
      keepUnusedDataFor: 3600, // 1 hour cache
    }),

    // Recent quotes the user already got for one hotel — newest per stay, within
    // the last `maxAgeDays` (default 7 on the backend). Powers the recent-quotes
    // pop-under on the hotel sheet.
    getHotelPriceQuotes: builder.query<
      HotelQuote[],
      { hotelRef: string; maxAgeDays?: number }
    >({
      query: ({ hotelRef, maxAgeDays }) => ({
        url: `/api/price_quotes`,
        params: {
          hotel_ref: hotelRef,
          ...(maxAgeDays ? { max_age_days: maxAgeDays } : {}),
        },
      }),
      transformResponse: (response: { data: HotelQuote[] }) => response.data,
      providesTags: (result, _error, { hotelRef }) =>
        result
          ? [
              ...result.map((q) => ({
                type: "PriceQuote" as const,
                id: q.ref,
              })),
              { type: "PriceQuote", id: `HOTEL_LIST_${hotelRef}` },
            ]
          : [{ type: "PriceQuote", id: `HOTEL_LIST_${hotelRef}` }],
    }),

    // Fetch all quotes for a chat (no expiry filter — frontend handles expiry)
    getChatQuotes: builder.query<PriceQuoteResponse[], { chatId: number }>({
      query: ({ chatId }) => ({
        url: `/api/chats/${chatId}/quotes`,
      }),
      transformResponse: (response: { data: PriceQuoteResponse[] }) =>
        response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map((q) => ({
                type: "PriceQuote" as const,
                id: q.ref,
              })),
              { type: "PriceQuote", id: "CHAT_LIST" },
            ]
          : [{ type: "PriceQuote", id: "CHAT_LIST" }],
    }),
  }),
})

export const {
  useLazyGetPriceQuoteQuery,
  useGetPriceQuoteQuery,
  useGetPriceQuoteWithLivePriceQuery,
  useGetHotelPriceQuotesQuery,
  useGetChatQuotesQuery,
  useLazyGetChatQuotesQuery,
} = priceQuotesApi
